import { Component, type ErrorInfo, type ReactNode } from 'react';
import {
  getThemeForDesignMode,
  parseStoredDesignMode,
  DESIGN_MODE_STORAGE_KEY_V2,
  DESIGN_MODE_STORAGE_KEY_V1,
} from '../config/theme';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('App error:', error, errorInfo);
    // Chunk load failure = stale deployment; reload fetches fresh HTML + correct chunks
    const isChunkError =
      error?.message?.includes('Failed to fetch dynamically imported module') ||
      error?.message?.includes('Loading chunk') ||
      error?.message?.includes('Loading CSS chunk');
    if (isChunkError) {
      window.location.reload();
      return;
    }
  }

  render() {
    if (this.state.hasError && this.state.error) {
      const v2 =
        typeof localStorage !== 'undefined' ? localStorage.getItem(DESIGN_MODE_STORAGE_KEY_V2) : null;
      const v1 =
        typeof localStorage !== 'undefined' ? localStorage.getItem(DESIGN_MODE_STORAGE_KEY_V1) : null;
      const mode = parseStoredDesignMode(v2, v1);
      const theme = getThemeForDesignMode(mode);
      return (
        <div style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '2rem',
          backgroundColor: theme.bg,
          color: theme.textPrimary,
          fontFamily: 'system-ui, sans-serif'
        }}>
          <div style={{
            maxWidth: '32rem',
            padding: '2rem',
            backgroundColor: theme.bgCard,
            borderRadius: '0.5rem',
            boxShadow: '0 12px 18px -3px rgba(0,0,0,0.3)',
            border: `1px solid ${theme.border}`
          }}>
            <h1 style={{ fontSize: theme.headingSize, fontWeight: theme.headingWeight, color: theme.danger ?? '#dc2626', marginBottom: '1rem' }}>
              Something went wrong
            </h1>
            <p style={{ color: theme.textMuted ?? '#6b7280', marginBottom: '1rem' }}>
              {this.state.error.message}
            </p>
            {/* Stack traces can leak internals; show them only in development. */}
            {import.meta.env.DEV && (
              <pre style={{
                padding: '1rem',
                backgroundColor: theme.inputBg ?? theme.bg ?? '#f3f4f6',
                color: theme.textPrimary,
                borderRadius: '0.375rem',
                fontSize: '0.75rem',
                overflow: 'auto',
                marginBottom: '1rem',
                border: `1px solid ${theme.border}`
              }}>
                {this.state.error.stack}
              </pre>
            )}
            <button
              onClick={() => window.location.reload()}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: theme.btnPrimaryBg ?? theme.primary,
                color: theme.btnPrimaryColor ?? 'white',
                border: 'none',
                borderRadius: '0.375rem',
                cursor: 'pointer'
              }}
            >
              Reload page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
